import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Element, scroller } from 'react-scroll';
import { formatCurrency, fullName } from 'utils';
import Button from 'common/button';
import Modal from 'common/modal';
import Fundraiser from './fundraiser';
import Tip from './tip';
import Match from './match';
import ValiantNameEntry from './valiant-entry';
import StripeContainer from './card-entry';

class EnterPayment extends Component {
    static propTypes = {
        onSubmit: PropTypes.func.isRequired,
        campaign: PropTypes.shape({
            name: PropTypes.string.isRequired,
            enableFundraisers: PropTypes.bool.isRequired,
            metricName: PropTypes.shape({
                singular: PropTypes.string.isRequired,
            }).isRequired,
            stats: PropTypes.shape({
                performanceRemaining: PropTypes.number.isRequired,
                performanceActualTotal: PropTypes.number.isRequired,
            }).isRequired,
            recipient: PropTypes.shape({
                name: PropTypes.string,
                type: PropTypes.string,
                collectAddresses: PropTypes.bool,
            }).isRequired,
            owner: PropTypes.shape({
                firstName: PropTypes.string,
                lastName: PropTypes.string,
            }).isRequired,
            shouldDisplayBenefitInHero: PropTypes.bool.isRequired,
            benefits: PropTypes.string,
        }).isRequired,
        contribution: PropTypes.shape({
            type: PropTypes.oneOf(['pledge', 'donate']).isRequired,
            amount: PropTypes.number.isRequired,
            fundraiser: PropTypes.string,
            backpledge: PropTypes.bool,
        }).isRequired,
        enableTips: PropTypes.bool.isRequired,
        hasMatch: PropTypes.bool.isRequired,
        isValiantCampaign: PropTypes.bool.isRequired,
        showFlash: PropTypes.func.isRequired,
    };

    state = {
        // eslint-disable-next-line react/destructuring-assignment
        fundraiser: this.props.contribution.fundraiser || null,
        isShowingStripeModal: false,
        tip: null,
        displayName: '',

        // eslint-disable-next-line react/destructuring-assignment
        isMatched: this.props.hasMatch ? true : undefined,
    };

    get isPledge() {
        const { contribution } = this.props;
        return contribution.type === 'pledge';
    }

    get lineLabel() {
        const { campaign } = this.props;
        return this.isPledge ? `Pledge per ${campaign.metricName.singular}` : `One-time donation`;
    }

    get subtotal() {
        const { campaign, contribution } = this.props;
        const { amount } = contribution;
        const { performanceRemaining, performanceActualTotal } = campaign.stats;

        if (!this.isPledge) return contribution.amount;

        let subtotal = 0;
        subtotal += amount * performanceRemaining;

        if (contribution.backpledge) {
            subtotal += amount * performanceActualTotal;
        }

        return subtotal;
    }

    get totalLabel() {
        return this.isPledge ? 'Your estimated total' : 'Your total';
    }

    get total() {
        const { tip } = this.state;
        return tip ? this.subtotal + tip : this.subtotal;
    }

    get recipientName() {
        const { campaign } = this.props;
        const { recipient, owner, shouldDisplayBenefitInHero, benefits } = campaign;

        if (shouldDisplayBenefitInHero) {
            return benefits;
        }

        return recipient.type === 'individual'
            ? fullName(owner.firstName, owner.lastName)
            : recipient.name;
    }

    handlePaymentInfo = ({ user, token, card }) => {
        const { onSubmit } = this.props;
        const { isMatched, fundraiser, displayName, tip } = this.state;

        this.toggleStripeModal();

        onSubmit({
            isMatched,
            fundraiser,
            displayName,
            tip,
            token,
            card,
            user,
        });
    };

    handleFundraiserSelect = fundraiser => this.setState({ fundraiser });

    handleTipSelect = tip => this.setState({ tip });

    handleToggleMatch = isMatched => this.setState({ isMatched });

    handleDisplayNameUpdate = displayName => this.setState({ displayName });

    toggleStripeModal = () => {
        this.setState(prev => ({ isShowingStripeModal: !prev.isShowingStripeModal }));
    };

    render() {
        const {
            campaign,
            contribution,
            enableTips,
            hasMatch,
            isValiantCampaign,
            showFlash,
        } = this.props;
        const { displayName, fundraiser, isMatched, isShowingStripeModal, tip } = this.state;

        return (
            <div className="mx-auto" css={{ maxWidth: 480 }}>
                <div className="bg-white rounded-lg shadow-md py-12 px-8 xxxl:py-16 md:px-24">
                    <div className="text-center border-b-3 border-gray-20 pb-8 mb-8">
                        <p className="text-12 uppercase text-gray-30 tracking-wide block font-bold">
                            Contributing to
                        </p>
                        <h1 className="text-18 font-bold truncate text-gray-70 mt-2">
                            {campaign.name}
                        </h1>

                        {campaign.enableFundraisers && (
                            <Fundraiser
                                selected={fundraiser}
                                onSelect={this.handleFundraiserSelect}
                            />
                        )}
                    </div>

                    {isValiantCampaign && (
                        <ValiantNameEntry
                            name={displayName}
                            onChange={this.handleDisplayNameUpdate}
                            onBlur={() => {
                                if (!displayName) return;
                                scroller.scrollTo('js-scroll-amount', {
                                    duration: 500,
                                    smooth: true,
                                    offset: -125,
                                });
                            }}
                        />
                    )}

                    <div className="border-b-3 border-gray-20 pb-8 mb-8">
                        <Element name="js-scroll-amount" />
                        <div className="flex justify-between items-center mb-8 font-medium text-16 text-gray-70">
                            <p>{this.lineLabel}</p>
                            <p className="text-right">{formatCurrency(contribution.amount)}</p>
                        </div>

                        {this.isPledge && (
                            <div className="flex justify-between items-center mb-8 font-medium text-16 text-gray-70">
                                <p className="text-gray-30">Estimated subtotal</p>
                                <p className="text-right">USD {formatCurrency(this.subtotal)}</p>
                            </div>
                        )}

                        <button
                            type="button"
                            className="bg-gray-10 hover:bg-gray-20 transition rounded text-14 block w-full py-8 px-4 font-medium text-gray-70"
                            onClick={this.toggleStripeModal}
                        >
                            <span className="block">Credit/Debit card</span>
                            <span className="block text-12 text-gray-40 mt-2">
                                Click or tap to enter information
                            </span>
                        </button>
                    </div>

                    {enableTips && (
                        <Tip
                            onSelect={this.handleTipSelect}
                            contribution={this.subtotal}
                            showFlash={showFlash}
                            tip={tip}
                            recipientName={this.recipientName}
                        />
                    )}

                    <div className="flex justify-between items-center font-medium text-16 text-blue-50">
                        <p>{this.totalLabel}</p>
                        <p className="text-right">USD {formatCurrency(this.total)}</p>
                    </div>

                    {hasMatch && <Match isMatched={isMatched} onChange={this.handleToggleMatch} />}
                </div>

                <div className="mt-8 lg:mx-16">
                    <Button
                        type="button"
                        size="md"
                        color="blue"
                        className="text-16 font-medium w-full"
                        onClick={this.toggleStripeModal}
                        data-testid="card"
                    >
                        Enter Credit/Debit Card
                    </Button>
                </div>
                <Modal close={this.toggleStripeModal} isOpen={isShowingStripeModal}>
                    <StripeContainer
                        onSubmit={this.handlePaymentInfo}
                        shouldCollectAddress={campaign.recipient.collectAddresses}
                    />
                </Modal>
            </div>
        );
    }
}

export default EnterPayment;
